import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";

import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import {
  CurrencyType,
  DefaultCurrencyPerLocation,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { panelRadiusTypes } from "@bitwarden/web-vault/app/components/dropdown/dropdown-select/dropdown-select.component";

/**
 * CountriesComponent is a component that provides a form field for selecting countries.
 * It uses Angular Material's autocomplete feature to suggest countries as the user types.
 * The selected country is emitted as events.
 * TODO: Add a feature to select multiple countries.
 * TODO: Add a feature of custom styling.
 */
@Component({
  selector: "app-currencies",
  templateUrl: "./currencies.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrenciesComponent implements OnChanges {
  @Input() label: string;
  @Input() defaultCurrency: CurrencyType | string; // todo this should only be string
  @Input() disabled = false;
  @Input() isRequired = false;
  @Input() customShadowClass = "tw-shadow-dropdown";
  @Input() panelRadius: panelRadiusTypes = "default";
  @Input() detachPanel = false;
  @Output() onSelectCurrency: EventEmitter<string> = new EventEmitter<string>();

  private role = inject(RoleAccessService);
  private userStoreService = inject(UserStoreService);
  private appState = inject(AppStateStoreService);
  private cdr = inject(ChangeDetectorRef);

  allCurrencies: CurrencyType[] = this.role
    .getRoleAccess()
    ?.getClaim()
    ?.forex_currency?.slice()
    .sort();
  filteredCurrencies: CurrencyType[] = this.allCurrencies;
  currency: string;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.defaultCurrency) {
      this.setCurrency();
      this.cdr.markForCheck();
    }
  }

  private setCurrency() {
    if (!this.defaultCurrency) {
      const userLocation = this.userStoreService.preferences.preferenceView().userLocation;
      this.currency = DefaultCurrencyPerLocation[userLocation];
    } else {
      this.currency = this.defaultCurrency;
    }
    this.filterCurrenciesInWizard();
  }

  /** use the currency of the region only, in wizard */
  filterCurrenciesInWizard() {
    if (this.appState.wizard().isVisible) {
      this.filteredCurrencies = this.allCurrencies.filter((currency) => currency === this.currency);
      this.currency = this.defaultCurrency;
    }
  }

  /**
   * Emits the selectedCountry event with the provided country.
   * @param {string} currency - The country that was selected.
   */
  currencySelected(currency: string) {
    if (this.onSelectCurrency) {
      this.onSelectCurrency.emit(currency);
    }
  }

  /**
   * Clears the selection of the country.
   * This method is used to emit a null value to the `selectedCountry` event emitter,
   * indicating that the country selection has been cleared.
   */
  selectionCleared() {
    this.onSelectCurrency.emit(null);
  }
}
